/* ==========================================================================
   EVE Füllwatte-Rechner – Logik und Oberfläche
   Ohne fremde Bibliotheken. Rechenteil als reine Funktionen, geprüft mit node --test (test/logik.test.mjs).
   Richtformeln hergeleitet und geprüft mit werkzeuge/richtformel.py → daten/richtformel.json.
   ========================================================================== */
(function (global) {
  'use strict';

  /* Richtformeln (Stand 2026-10-17). Ändern nur über werkzeuge/richtformel.py; ein Test vergleicht die Werte.
     Kissen: aus den 11 Füllgewichten der Eve-Kopfkissen (Basic, Diamond), RMS-Abweichung 12 %.
     Kuscheltier: aus 18 Angaben in Näh- und Häkelanleitungen (recherche.md 1b); streut stark. */
  var RICHTFORMEL = {
    kissen: { a: 1830.9, b: 0.914, spanne: [0.82, 1.24], festigkeit: { locker: 0.82, wie_eve: 1.0, prall: 1.2 } },
    kuscheltier: { a: 0.1304, b: 2.073, spanne: [0.68, 1.43], flach: 0.44 }
  };

  var GRENZEN = { mass: [10, 200], groesse: [5, 150], anzahl: [1, 100] };
  var EINHEIT = 50; // g – alle Packungen sind Vielfache davon

  function aufZehn(g) { return Math.ceil(g / 10) * 10; }

  /** „250 Gramm“, „1 Kilogramm“, „1 kg“, „500 g“ → Gramm (oder null). */
  function grammAusText(text) {
    var m = String(text || '').replace(',', '.').match(/(\d+(?:\.\d+)?)\s*(kilogramm|kg|gramm|g)\b/i);
    if (!m) return null;
    var zahl = parseFloat(m[1]);
    return Math.round(/^k/i.test(m[2]) ? zahl * 1000 : zahl);
  }

  /** Füllmenge je Kissen: Richtwert und Spanne in Gramm. */
  function kissenGramm(breite, laenge, festigkeit) {
    var f = RICHTFORMEL.kissen;
    var faktor = f.festigkeit[festigkeit] || 1;
    var basis = f.a * Math.pow((breite * laenge) / 10000, f.b) * faktor;
    return { richtwert: aufZehn(basis), von: aufZehn(basis * f.spanne[0]), bis: aufZehn(basis * f.spanne[1]) };
  }

  /** Füllmenge je Kuscheltier nach größtem Maß in cm. */
  function kuscheltierGramm(groesse, form) {
    var f = RICHTFORMEL.kuscheltier;
    var basis = f.a * Math.pow(groesse, f.b) * (form === 'flach' ? f.flach : 1);
    return { richtwert: aufZehn(basis), von: aufZehn(basis * f.spanne[0]), bis: aufZehn(basis * f.spanne[1]) };
  }

  /** Verfügbare Packungen eines Produkts: [{variante, gramm, preis}] aufsteigend. */
  function packungen(produkt) {
    return (produkt && produkt.varianten || [])
      .filter(function (v) { return v.verfuegbar; })
      .map(function (v) { return { variante: v, gramm: grammAusText(v.titel) || grammAusText((v.optionen || []).join(' ')), preis: v.preis }; })
      .filter(function (p) { return p.gramm; })
      .sort(function (a, b) { return a.gramm - b.gramm; });
  }

  /** Günstigste Kombination, die mindestens `bedarf` Gramm abdeckt.
      Bei gleichem Preis: weniger Packungen, dann weniger Rest. */
  function packungWaehlen(bedarf, produkt) {
    var liste = packungen(produkt);
    if (!liste.length || !(bedarf > 0)) return null;
    var ziel = Math.ceil(bedarf / EINHEIT);
    var groesste = Math.ceil(liste[liste.length - 1].gramm / EINHEIT);
    var obergrenze = ziel + groesste;
    var kosten = new Array(obergrenze + 1).fill(Infinity);
    var stueck = new Array(obergrenze + 1).fill(Infinity);
    var wahl = new Array(obergrenze + 1).fill(-1);
    kosten[0] = 0;
    stueck[0] = 0;
    for (var u = 1; u <= obergrenze; u++) {
      for (var i = 0; i < liste.length; i++) {
        var e = Math.round(liste[i].gramm / EINHEIT);
        if (e > u || kosten[u - e] === Infinity) continue;
        var k = kosten[u - e] + liste[i].preis;
        var s = stueck[u - e] + 1;
        if (k < kosten[u] || (k === kosten[u] && s < stueck[u])) { kosten[u] = k; stueck[u] = s; wahl[u] = i; }
      }
    }
    var best = -1;
    for (var x = ziel; x <= obergrenze; x++) {
      if (kosten[x] === Infinity) continue;
      if (best < 0 || kosten[x] < kosten[best] || (kosten[x] === kosten[best] && stueck[x] < stueck[best])) best = x;
    }
    if (best < 0) return null;
    var zaehler = {};
    for (var y = best; y > 0; y -= Math.round(liste[wahl[y]].gramm / EINHEIT)) {
      zaehler[wahl[y]] = (zaehler[wahl[y]] || 0) + 1;
    }
    var posten = Object.keys(zaehler).map(function (idx) {
      return { variante: liste[idx].variante, gramm: liste[idx].gramm, anzahl: zaehler[idx] };
    }).sort(function (a, b) { return b.gramm - a.gramm; });
    var gramm = posten.reduce(function (s, p) { return s + p.gramm * p.anzahl; }, 0);
    return { posten: posten, gramm: gramm, preis: kosten[best], rest: gramm - bedarf };
  }

  function ganzzahl(wert) {
    var n = Number(String(wert).replace(',', '.'));
    return isFinite(n) ? n : NaN;
  }

  /** Eingaben prüfen. Gibt {feld: text} zurück, leer wenn alles passt. */
  function pruefe(e) {
    var f = {};
    function bereich(feld, wert, grenzen, text) {
      var n = ganzzahl(wert);
      if (!(n >= grenzen[0] && n <= grenzen[1])) f[feld] = text;
    }
    if (e.modus === 'kuscheltier') {
      bereich('groesse', e.groesse, GRENZEN.groesse, 'Bitte geben Sie eine Größe zwischen 5 und 150 cm ein.');
    } else {
      bereich('breite', e.breite, GRENZEN.mass, 'Bitte geben Sie eine Breite zwischen 10 und 200 cm ein.');
      bereich('laenge', e.laenge, GRENZEN.mass, 'Bitte geben Sie eine Länge zwischen 10 und 200 cm ein.');
    }
    var a = ganzzahl(e.anzahl);
    if (!(a >= GRENZEN.anzahl[0] && a <= GRENZEN.anzahl[1] && Math.floor(a) === a)) f.anzahl = 'Bitte geben Sie eine ganze Zahl von 1 bis 100 ein.';
    return f;
  }

  /** Reine Funktion: Eingaben + Produktdaten → Ergebnis. */
  function berechne(e, daten) {
    var fehler = pruefe(e);
    if (Object.keys(fehler).length) return { fehler: fehler };
    var anzahl = ganzzahl(e.anzahl);
    var je = e.modus === 'kuscheltier'
      ? kuscheltierGramm(ganzzahl(e.groesse), e.form)
      : kissenGramm(ganzzahl(e.breite), ganzzahl(e.laenge), e.festigkeit || 'wie_eve');
    var produkte = (daten && daten.produkte) || [];
    var produkt = produkte.filter(function (p) { return p.handle === e.produkt; })[0] || produkte[0] || null;
    var gesamt = je.richtwert * anzahl;
    var gesamtBis = je.bis * anzahl;
    var packung = produkt ? packungWaehlen(gesamt, produkt) : null;
    var packungBis = produkt ? packungWaehlen(gesamtBis, produkt) : null;
    if (packung && packungBis && packungBis.gramm === packung.gramm) packungBis = null;
    return {
      fehler: null,
      je: je,
      anzahl: anzahl,
      gesamt: gesamt,
      gesamtVon: je.von * anzahl,
      gesamtBis: gesamtBis,
      produkt: produkt,
      packung: packung,
      packungBis: packungBis
    };
  }

  /* ---------- Texte ---------- */

  function preisText(cent) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cent / 100);
  }

  /** bis 9.999 g in Gramm („2.080 g“), darüber in Kilogramm mit einer Stelle („12,4 kg“). */
  function grammText(g) {
    return g < 10000
      ? g.toLocaleString('de-DE') + ' g'
      : (Math.ceil(g / 100) / 10).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' kg';
  }

  function grundpreisText(cent, gramm) {
    return preisText(Math.round(cent / (gramm / 1000))) + '/kg';
  }

  function postenText(packung) {
    return packung.posten.map(function (p) { return (p.anzahl > 1 ? p.anzahl + ' × ' : '') + p.variante.titel; }).join(' + ');
  }

  var QUELLE = {
    kissen: 'Richtwert, berechnet aus den Füllgewichten unserer eigenen Kopfkissen in elf Größen. Wie viel Sie wirklich brauchen, hängt davon ab, wie fest Sie stopfen.',
    kuscheltier: 'Richtwert aus Angaben in Näh- und Häkelanleitungen. Kuscheltiere werden sehr unterschiedlich fest gestopft – planen Sie lieber etwas mehr ein.'
  };

  /* ======================================================================
     Oberfläche
     ====================================================================== */

  function Rechner(root) {
    this.root = root;
    var datenEl = root.querySelector('[data-fr-daten]');
    try { this.daten = JSON.parse(datenEl ? datenEl.textContent : '{}'); } catch (e) { this.daten = { produkte: [] }; }
    this.form = root.querySelector('[data-fr-form]');
    this.ergebnis = root.querySelector('[data-fr-ergebnis]');
    this.ansage = root.querySelector('[data-fr-ansage]');
    this.materialOrt = root.querySelector('[data-fr-material]');
    this.timer = null;
    this.materialBauen();
    this.verdrahten();
    this.form.hidden = false;
    this.ergebnis.hidden = false;
    this.modusZeigen();
    this.rechnen(false);
  }

  Rechner.prototype.feld = function (name) { return this.form.elements[name]; };

  /** Wert eines Feldes oder einer Radiogruppe (RadioNodeList hat ebenfalls .value). */
  Rechner.prototype.wert = function (name) {
    var f = this.feld(name);
    return f ? f.value : undefined;
  };

  Rechner.prototype.materialBauen = function () {
    var produkte = this.daten.produkte || [];
    var ort = this.materialOrt;
    if (!ort) return;
    if (produkte.length < 2) { ort.hidden = true; return; }
    var aktuell = this.root.getAttribute('data-aktuell');
    var name = 'fr-material-' + (this.root.getAttribute('data-section-id') || 'x');
    var liste = ort.querySelector('[data-fr-material-liste]');
    produkte.forEach(function (p, i) {
      var label = document.createElement('label');
      label.className = 'eve-fr__chip';
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'produkt';
      input.value = p.handle;
      input.className = 'eve-fr__radio';
      input.checked = aktuell ? p.handle === aktuell : i === 0;
      var span = document.createElement('span');
      span.textContent = p.label || p.titel;
      label.appendChild(input);
      label.appendChild(span);
      liste.appendChild(label);
    });
    if (!liste.querySelector('input:checked')) liste.querySelector('input').checked = true;
    ort.hidden = false;
  };

  Rechner.prototype.verdrahten = function () {
    var self = this;
    this.form.addEventListener('submit', function (ev) { ev.preventDefault(); self.rechnen(true); });
    this.form.addEventListener('input', function (ev) {
      if (ev.target.name === 'modus') self.modusZeigen();
      self.schnellMarkieren();
      self.rechnen(false);
    });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fr-schnell]'), function (b) {
      b.addEventListener('click', function () {
        var werte = b.getAttribute('data-fr-schnell').split('x');
        if (werte.length === 2) { self.feld('breite').value = werte[0]; self.feld('laenge').value = werte[1]; }
        else self.feld('groesse').value = werte[0];
        self.schnellMarkieren();
        self.rechnen(false);
      });
    });
    var kaufen = this.root.querySelector('[data-fr-kaufen]');
    kaufen.addEventListener('click', function () { self.inDenWarenkorb(kaufen); });
  };

  Rechner.prototype.modusZeigen = function () {
    var modus = this.wert('modus');
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fr-teil]'), function (t) {
      t.hidden = t.getAttribute('data-fr-teil') !== modus;
    });
    this.schnellMarkieren();
  };

  Rechner.prototype.schnellMarkieren = function () {
    var b = this.wert('breite');
    var l = this.wert('laenge');
    var g = this.wert('groesse');
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fr-schnell]'), function (k) {
      var w = k.getAttribute('data-fr-schnell').split('x');
      var an = w.length === 2 ? (w[0] === b && w[1] === l) || (w[0] === l && w[1] === b) : w[0] === g;
      k.setAttribute('aria-pressed', an ? 'true' : 'false');
    });
  };

  Rechner.prototype.eingaben = function () {
    return {
      modus: this.wert('modus'),
      breite: this.wert('breite'),
      laenge: this.wert('laenge'),
      festigkeit: this.wert('festigkeit'),
      groesse: this.wert('groesse'),
      form: this.wert('form'),
      anzahl: this.wert('anzahl'),
      produkt: this.wert('produkt')
    };
  };

  Rechner.prototype.fehlerZeigen = function (fehler) {
    var root = this.root;
    ['breite', 'laenge', 'groesse', 'anzahl'].forEach(function (name) {
      var feld = root.querySelector('[name="' + name + '"]');
      var meldung = root.querySelector('[data-fr-fehler="' + name + '"]');
      if (!feld || !meldung) return;
      var text = fehler && fehler[name];
      if (text) {
        feld.setAttribute('aria-invalid', 'true');
        meldung.textContent = text;
        meldung.hidden = false;
      } else {
        feld.removeAttribute('aria-invalid');
        meldung.textContent = '';
        meldung.hidden = true;
      }
    });
  };

  Rechner.prototype.setze = function (sel, text) {
    var e = this.ergebnis.querySelector(sel);
    if (e) e.textContent = text;
    return e;
  };

  Rechner.prototype.rechnen = function (ansagen) {
    var e = this.eingaben();
    var r = berechne(e, this.daten);
    this.letztes = r;
    var box = this.ergebnis;
    this.fehlerZeigen(r.fehler);
    box.classList.toggle('is-leer', !!r.fehler);
    var kaufen = box.querySelector('[data-fr-kaufen]');
    var link = box.querySelector('[data-fr-link]');
    var packungEl = box.querySelector('[data-fr-packung]');
    var hinweis = box.querySelector('[data-fr-hinweis]');

    if (r.fehler) {
      this.setze('[data-fr-wert]', '–');
      this.setze('[data-fr-fuer]', 'Bitte prüfen Sie Ihre Eingaben.');
      this.setze('[data-fr-spanne]', '');
      packungEl.hidden = true;
      kaufen.hidden = true;
      link.hidden = true;
      hinweis.hidden = true;
      clearTimeout(this.timer);
      this.ansage.textContent = '';
      return;
    }

    var kuscheltier = e.modus === 'kuscheltier';
    var was = kuscheltier
      ? (r.anzahl === 1 ? 'ein Kuscheltier' : r.anzahl + ' Kuscheltiere') + ' mit ' + Number(e.groesse).toLocaleString('de-DE') + ' cm'
      : (r.anzahl === 1 ? 'ein Kissen' : r.anzahl + ' Kissen') + ' ' + e.breite + ' × ' + e.laenge + ' cm';
    this.setze('[data-fr-wert]', 'ca. ' + grammText(r.gesamt));
    this.setze('[data-fr-fuer]', 'für ' + was + (r.anzahl > 1 ? ' (je ca. ' + grammText(r.je.richtwert) + ')' : ''));
    this.setze('[data-fr-spanne]', 'Je nach Füllung etwa ' + grammText(r.gesamtVon) + ' bis ' + grammText(r.gesamtBis) + '.');
    this.setze('[data-fr-quelle]', QUELLE[kuscheltier ? 'kuscheltier' : 'kissen']);

    if (r.packung) {
      var p = r.produkt;
      var bild = box.querySelector('[data-fr-bild]');
      if (bild) {
        if (p.bild) { bild.src = p.bild; bild.hidden = false; } else bild.hidden = true;
      }
      this.setze('[data-fr-packung-titel]', (p.label || p.titel) + ': ' + postenText(r.packung));
      this.setze('[data-fr-packung-preis]', preisText(r.packung.preis) + ' · ' + grundpreisText(r.packung.preis, r.packung.gramm));
      this.setze('[data-fr-rest]', r.packung.rest >= 50 ? 'Rest ca. ' + grammText(aufZehn(r.packung.rest)) + ' – zum Nachfüllen oder für das nächste Projekt.' : '');
      packungEl.hidden = false;
      kaufen.hidden = false;
      kaufen.disabled = false;
      link.hidden = false;
      link.href = p.url + (p.url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + r.packung.posten[0].variante.id;
      if (r.packungBis) {
        hinweis.textContent = 'Stopfen Sie besonders fest, nehmen Sie lieber ' + postenText(r.packungBis) + ' (' + preisText(r.packungBis.preis) + ').';
        hinweis.hidden = false;
      } else {
        hinweis.hidden = true;
      }
    } else {
      packungEl.hidden = true;
      kaufen.hidden = true;
      link.hidden = !r.produkt;
      if (r.produkt) link.href = r.produkt.url;
      hinweis.textContent = 'Dieses Füllmaterial ist gerade nicht lieferbar. Schreiben Sie uns – wir sagen Ihnen, wann es wieder da ist.';
      hinweis.hidden = false;
    }

    // Ansage für Screenreader: verzögert, damit nicht jede Ziffer vorgelesen wird
    var self = this;
    var text = 'Richtwert ' + grammText(r.gesamt) + ' für ' + was + '.' + (r.packung ? ' Passende Packung: ' + postenText(r.packung) + ', ' + preisText(r.packung.preis) + '.' : '');
    clearTimeout(this.timer);
    this.timer = setTimeout(function () { self.ansage.textContent = text; }, ansagen ? 0 : 700);
  };

  Rechner.prototype.inDenWarenkorb = function (knopf) {
    var r = this.letztes;
    if (!r || !r.packung) return;
    var root = this.root;
    var fehlerEl = root.querySelector('[data-fr-kauf-fehler]');
    var ziel = (root.getAttribute('data-cart-add-url') || '/cart/add') + '.js';
    var warenkorb = root.getAttribute('data-cart-url') || '/cart';
    var text = knopf.textContent;
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
    knopf.textContent = 'Wird hinzugefügt …';
    fehlerEl.hidden = true;
    fetch(ziel, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: r.packung.posten.map(function (p) { return { id: p.variante.id, quantity: p.anzahl }; }) })
    }).then(function (antwort) {
      if (!antwort.ok) throw new Error(String(antwort.status));
      global.location.href = warenkorb;
    }).catch(function () {
      knopf.disabled = false;
      knopf.removeAttribute('aria-busy');
      knopf.textContent = text;
      fehlerEl.textContent = 'Das hat nicht geklappt. Bitte wählen Sie die Packung über „Zum Artikel“ aus.';
      fehlerEl.hidden = false;
    });
  };

  var api = {
    RICHTFORMEL: RICHTFORMEL,
    grammAusText: grammAusText,
    kissenGramm: kissenGramm,
    kuscheltierGramm: kuscheltierGramm,
    packungWaehlen: packungWaehlen,
    pruefe: pruefe,
    berechne: berechne,
    grammText: grammText,
    QUELLE: QUELLE
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    global.EveFuellwatteRechner = api;
    var start = function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-eve-fr]:not([data-fr-bereit])'), function (root) {
        root.setAttribute('data-fr-bereit', '');
        new Rechner(root);
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
    document.addEventListener('shopify:section:load', start);
  }
})(typeof window !== 'undefined' ? window : globalThis);
